import { FormEvent, useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { fetchJson } from "@/lib/fetch-json"
import type { PageProps } from "@/types/inertia"

type UploadState =
  | { phase: "idle" }
  | { phase: "uploading"; file: string; percent: number }
  | { phase: "saving" }
  | { phase: "error"; message: string }

export default function AdminReleaseNew() {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const [version, setVersion] = useState("")
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [dmgFile, setDmgFile] = useState<File | null>(null)
  const [ymlFile, setYmlFile] = useState<File | null>(null)
  const [state, setState] = useState<UploadState>({ phase: "idle" })

  const busy = state.phase === "uploading" || state.phase === "saving"

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!zipFile || !ymlFile || busy) return

    try {
      const zipKey = await uploadToR2(version, zipFile, "application/zip", (percent) =>
        setState({ phase: "uploading", file: zipFile.name, percent }),
      )

      let dmgKey: string | null = null
      if (dmgFile) {
        dmgKey = await uploadToR2(version, dmgFile, "application/octet-stream", (percent) =>
          setState({ phase: "uploading", file: dmgFile.name, percent }),
        )
      }

      const latestMacYml = await ymlFile.text()

      setState({ phase: "saving" })
      router.post(
        "/admin/releases",
        {
          version,
          zip_key: zipKey,
          dmg_key: dmgKey,
          latest_mac_yml: latestMacYml,
        },
        {
          onError: () => setState({ phase: "idle" }),
          onFinish: () => setState((s) => (s.phase === "saving" ? { phase: "idle" } : s)),
        },
      )
    } catch (error) {
      setState({ phase: "error", message: error instanceof Error ? error.message : "Upload failed." })
    }
  }

  return (
    <>
      <Head title="Upload Release">
        <meta name="description" content="Upload a new Flywrite desktop build to R2 and stage it for publishing." />
        <meta property="og:title" content="Upload Release" />
        <meta property="og:description" content="Upload a new Flywrite desktop build to R2 and stage it for publishing." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Upload Release"
          description="Files upload directly from your browser to R2, then the release is created as a draft."
          actions={
            <Link href="/admin/releases" className="text-sm text-ink-muted no-underline hover:text-ink-display">
              Back to releases
            </Link>
          }
        />

        <form onSubmit={submit} className="mt-8 max-w-lg space-y-6">
          <div className="space-y-2">
            <label htmlFor="release-version">Version</label>
            <Input
              id="release-version"
              required
              value={version}
              placeholder="1.4.0"
              aria-invalid={!!errors.version}
              onChange={(e) => setVersion(e.target.value)}
              disabled={busy}
            />
            <p className="text-xs text-ink-muted">
              Semver, matching the version electron-builder produced (e.g. 1.4.0).
            </p>
            {errors.version && <p className="text-xs text-danger-display">{errors.version}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="release-zip">Zip build (required)</label>
            <Input
              id="release-zip"
              type="file"
              accept=".zip"
              required
              onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            <p className="text-xs text-ink-muted">
              dist/Flywrite-&lt;version&gt;-mac.zip — this is what the auto-updater downloads.
            </p>
            {errors.zip_key && <p className="text-xs text-danger-display">{errors.zip_key}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="release-dmg">DMG installer (optional)</label>
            <Input
              id="release-dmg"
              type="file"
              accept=".dmg"
              onChange={(e) => setDmgFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            <p className="text-xs text-ink-muted">Served to customers for first-time installs.</p>
            {errors.dmg_key && <p className="text-xs text-danger-display">{errors.dmg_key}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="release-yml">latest-mac.yml (required)</label>
            <Input
              id="release-yml"
              type="file"
              accept=".yml,.yaml"
              required
              onChange={(e) => setYmlFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            <p className="text-xs text-ink-muted">
              From dist/ — provides the zip's sha512 checksum and size for the update feed.
            </p>
            {errors.latest_mac_yml && (
              <p className="text-xs text-danger-display">{errors.latest_mac_yml}</p>
            )}
            {errors.zip_sha512 && <p className="text-xs text-danger-display">{errors.zip_sha512}</p>}
            {errors.zip_size && <p className="text-xs text-danger-display">{errors.zip_size}</p>}
          </div>

          {state.phase === "uploading" && (
            <div className="rounded-lg border border-hairline bg-surface p-4">
              <p className="text-sm text-ink-body">
                Uploading {state.file}… {state.percent}%
              </p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-page">
                <div
                  className="h-full rounded-full bg-accent transition-[width]"
                  style={{ width: `${state.percent}%` }}
                />
              </div>
            </div>
          )}
          {state.phase === "saving" && <p className="text-sm text-ink-muted">Creating release…</p>}
          {state.phase === "error" && <p className="text-sm text-danger-display">{state.message}</p>}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={busy || !zipFile || !ymlFile}>
              {busy ? "Working…" : "Upload & create draft"}
            </Button>
          </div>
        </form>
      </AdminShell>
    </>
  )
}

async function uploadToR2(
  version: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void,
): Promise<string> {
  onProgress(0)
  const presign = await fetchJson<{ url?: string; key?: string; error?: string }>(
    "/admin/releases/presign_upload",
    { body: { filename: file.name, version, content_type: contentType } },
  )
  if (!presign.ok || !presign.data.url || !presign.data.key) {
    throw new Error(presign.data.error ?? "Couldn't presign the upload. Check the version format.")
  }

  await putWithProgress(presign.data.url, file, contentType, onProgress)
  return presign.data.key
}

function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", url)
    xhr.setRequestHeader("Content-Type", contentType)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Upload of ${file.name} failed (HTTP ${xhr.status}).`))
    }
    xhr.onerror = () => reject(new Error(`Upload of ${file.name} failed — network error.`))
    xhr.send(file)
  })
}
